import { ACCOUNT_DISPOSITION_STATUSES } from "shared/lib/constants/account";
import { clsxMerge } from "shared/lib/helpers";
import { getAccountDispositionStatusParams } from "shared/lib/helpers/disposition";
import { AccountBestLastTouchOutcomeGroupI } from "shared/lib/interfaces/account";
import { PropsWithClassNameI } from "shared/lib/interfaces/ui";
import { AccountDetailsSectionContainer } from "shared/ui/account-details/component-wrapper";

import { useAccountDetailsContext } from "@/modules/pipeline/account-details/context";
import { LabelValueV2 } from "@/components/modules/pipeline/account-details/shared/label-value";
import {
  formatDateInZone,
  getLeadTimeZone,
} from "@/modules/pipeline/account-details/shared/contact-local-time";
import { CallerNotesText } from "./caller-notes-text";

const DATE_FORMAT = "MMM D, h:mm A";

// GLE-8453: the label of each best last touch group
const BEST_LAST_TOUCH_LABELS: Record<
  AccountBestLastTouchOutcomeGroupI,
  string
> = {
  meeting_booked: "Meeting Booked",
  follow_up_required: "Follow-up Required",
  no_follow_up_required: "No Follow-up Required",
  do_not_call: "Do Not Call",
};

/**
 * GLE-8478
 * The last touch on the account, at the top of the card. It reads only the
 * account details response that the card loads on open. The account history
 * loads only when the History tab opens, so this block does not use it.
 *
 * A field with no data is not shown. With no data at all, the block is hidden.
 *
 * GLE-8453
 * When the backend sends a best last touch, the disposition row shows that
 * touch, not the latest one: its group and its date, then its notes.
 */
export const AccountLastTouchSection = ({ className }: PropsWithClassNameI) => {
  const { accountStatus, accountSummary, accountNote, contacts } =
    useAccountDetailsContext();

  // Dates show the lead's time zone by name (GLE-8478)
  const leadTimeZone = getLeadTimeZone(
    contacts,
    accountStatus?.primary_contact_id
  );

  const status = accountStatus?.status;
  const hasDisposition =
    !!status && status !== ACCOUNT_DISPOSITION_STATUSES.COLD_LEAD;

  const bestLastTouch = accountSummary?.best_last_touch;
  const bestLastTouchLabel = bestLastTouch
    ? BEST_LAST_TOUCH_LABELS[bestLastTouch.outcome_group]
    : undefined;
  const bestLastTouchNotes =
    (bestLastTouchLabel && bestLastTouch?.notes?.trim()) || null;

  const lastDisposition = bestLastTouchLabel
    ? [
        bestLastTouchLabel,
        formatDateInZone(bestLastTouch?.created_at, DATE_FORMAT, leadTimeZone),
      ]
        .filter(Boolean)
        .join(" · ")
    : hasDisposition
      ? getAccountDispositionStatusParams(accountStatus, false).label || null
      : null;

  const lastConversation = formatDateInZone(
    accountStatus?.last_conversation_time ||
      accountSummary?.last_conversation_date,
    DATE_FORMAT,
    leadTimeZone
  );

  const callerNotes = accountNote?.note?.trim() || null;

  // The status block already shows the next follow-up, so this block does not (GLE-8481)
  if (!lastDisposition && !lastConversation && !callerNotes) {
    return null;
  }

  return (
    <AccountDetailsSectionContainer
      title="Last touch"
      className={clsxMerge("bg-white", className)}
    >
      <div className="flex flex-col gap-3 py-1">
        <LabelValueV2 label="Last disposition" value={lastDisposition} />
        <LabelValueV2
          label="Disposition notes"
          value={
            bestLastTouchNotes && <CallerNotesText text={bestLastTouchNotes} />
          }
          valueClassName="line-clamp-4 whitespace-pre-line typography-body-4"
        />
        <LabelValueV2 label="Last conversation" value={lastConversation} />
        <LabelValueV2
          label="Caller notes"
          value={callerNotes && <CallerNotesText text={callerNotes} />}
          valueClassName="line-clamp-4 whitespace-pre-line typography-body-4"
        />
      </div>
    </AccountDetailsSectionContainer>
  );
};
